// Admin Settings Page Web Component - Index/List View
class AdminSettingsPage extends HTMLElement {
  constructor() {
    super();
    this.currentUser = null;
    this.isAdmin = false;
  }

  connectedCallback() {
    this.render();
    this.setupAuthListener();
  }

  setupAuthListener() {
    // Listen for auth-status-loaded event
    this.handleAuthStatusLoaded = (e) => {
      const { user, authenticated } = e.detail;

      if (authenticated && user) {
        this.currentUser = user;
        this.isAdmin = user.role === 'admin';

        if (this.isAdmin) {
          this.renderAdminSettings();
        } else {
          this.renderUnauthorized();
        }
      } else {
        this.currentUser = null;
        this.isAdmin = false;
        this.renderUnauthorized();
      }
    };

    document.addEventListener('auth-status-loaded', this.handleAuthStatusLoaded);

    // Listen for auth-success event
    this.handleAuthSuccess = (e) => {
      const { user } = e.detail;
      if (user) {
        this.currentUser = user;
        this.isAdmin = user.role === 'admin';

        if (this.isAdmin) {
          this.renderAdminSettings();
        } else {
          this.renderUnauthorized();
        }
      }
    };

    document.addEventListener('auth-success', this.handleAuthSuccess);
  }

  render() {
    this.innerHTML = `
      <div class="admin-settings-page">
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Checking admin permissions...</div>
        </div>
      </div>
    `;
  }

  renderUnauthorized() {
    this.innerHTML = `
      <div class="admin-settings-page">
        <div class="unauthorized-state">
          <div class="unauthorized-icon">🚫</div>
          <h2 class="unauthorized-title">Access Denied</h2>
          <p class="unauthorized-message">
            You don't have administrator privileges to access this section.
          </p>
          <a href="/" class="btn btn-primary">Return to Home</a>
        </div>
      </div>
    `;
  }

  renderAdminSettings() {
    this.innerHTML = `
      <div class="admin-settings-page">
        <div class="admin-settings-header">
          <h1>🛠️ Admin</h1>
          <p>Manage site administration and moderation</p>
        </div>

        <div class="admin-settings-list">
          <a href="/admin/audit-logs" class="admin-settings-item" data-route="/admin/audit-logs">
            <span class="item-icon">📋</span>
            <div class="item-content">
              <span class="item-title">Audit Logs</span>
              <span class="item-description">View system audit logs and user activity</span>
            </div>
            <span class="item-arrow">→</span>
          </a>
          
          <a href="/admin/users" class="admin-settings-item" data-route="/admin/users">
            <span class="item-icon">🎠</span>
            <div class="item-content">
              <span class="item-title">User Management</span>
              <span class="item-description">Manage users, roles, and permissions</span>
            </div>
            <span class="item-arrow">→</span>
          </a>
          
          <a href="/admin/prompts" class="admin-settings-item" data-route="/admin/prompts">
            <span class="item-icon">
              <svg class="menu-washing-machine" width="20" height="20" viewBox="0 0 16 16" fill="none">
                <!-- Machine body -->
                <rect x="2" y="2" width="12" height="12" rx="2" fill="currentColor" opacity="0.8"/>
                <!-- Door -->
                <circle cx="8" cy="8.5" r="4" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.6"/>
                <!-- Drum -->
                <circle cx="8" cy="8.5" r="2.5" fill="none" stroke="currentColor" stroke-width="1" opacity="0.4"/>
                <!-- Control buttons -->
                <circle cx="5" cy="4.5" r="0.5" fill="currentColor" opacity="0.7"/>
                <circle cx="7" cy="4.5" r="0.5" fill="currentColor" opacity="0.7"/>
                <!-- Door handle -->
                <rect x="11.5" y="7.5" width="0.5" height="2" rx="0.25" fill="currentColor" opacity="0.8"/>
              </svg>
            </span>
            <div class="item-content">
              <span class="item-title">Writing Prompts</span>
              <span class="item-description">Manage writing prompts and approvals</span>
            </div>
            <span class="item-arrow">→</span>
          </a>
          
          <a href="/admin/widgets" class="admin-settings-item" data-route="/admin/widgets">
            <span class="item-icon">🧩</span>
            <div class="item-content">
              <span class="item-title">Profile Widgets</span>
              <span class="item-description">Review and approve user-submitted widgets</span>
            </div>
            <span class="item-arrow">→</span>
          </a>
          
          <a href="/admin/stats" class="admin-settings-item" data-route="/admin/stats">
            <span class="item-icon">📊</span>
            <div class="item-content">
              <span class="item-title">Site Statistics</span>
              <span class="item-description">View site statistics and metrics</span>
            </div>
            <span class="item-arrow">→</span>
          </a>
          
          <a href="/admin/widget-flags" class="admin-settings-item" data-route="/admin/widget-flags">
            <span class="item-icon">🧩</span>
            <div class="item-content">
              <span class="item-title">Widget Flags</span>
              <span class="item-description">Review flagged profile widgets</span>
            </div>
            <span class="item-arrow">→</span>
          </a>
          
          <a href="/admin/image-flags" class="admin-settings-item" data-route="/admin/image-flags">
            <span class="item-icon">🖼️</span>
            <div class="item-content">
              <span class="item-title">Image Flags</span>
              <span class="item-description">Review and manage flagged images</span>
            </div>
            <span class="item-arrow">→</span>
          </a>
          
          <a href="/admin/blocked-domains" class="admin-settings-item" data-route="/admin/blocked-domains">
            <span class="item-icon">🚫</span>
            <div class="item-content">
              <span class="item-title">Blocked Domains</span>
              <span class="item-description">Manage site-wide blocked domains (images and widget links)</span>
            </div>
            <span class="item-arrow">→</span>
          </a>
          
          <a href="/admin/oauth-clients" class="admin-settings-item" data-route="/admin/oauth-clients">
            <span class="item-icon">🔐</span>
            <div class="item-content">
              <span class="item-title">OAuth Clients</span>
              <span class="item-description">Manage OAuth2 client applications</span>
            </div>
            <span class="item-arrow">→</span>
          </a>

          <a href="/admin/feature-flags" class="admin-settings-item" data-route="/admin/feature-flags">
            <span class="item-icon">🚩</span>
            <div class="item-content">
              <span class="item-title">Feature Flags</span>
              <span class="item-description">Turn site features on or off</span>
            </div>
            <span class="item-arrow">→</span>
          </a>
        </div>
      </div>
    `;
  }

  disconnectedCallback() {
    if (this.handleAuthStatusLoaded) {
      document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    }
    if (this.handleAuthSuccess) {
      document.removeEventListener('auth-success', this.handleAuthSuccess);
    }
  }

  setCurrentUser(user) {
    this.currentUser = user;
    this.isAdmin = user?.role === 'admin';
    if (this.isAdmin) {
      this.renderAdminSettings();
    } else {
      this.renderUnauthorized();
    }
  }
}

// Register the custom element
customElements.define('admin-settings-page', AdminSettingsPage);


